import { CanAccess } from '@modules/common/components/CanAccess';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useUpdateMetadata } from '@modules/integrationsV2/hooks/useUpdateMetadata';
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  MetadataDetailsViewFragment,
  PermissionOperation,
  PermissionResource,
} from 'generated/graphql';
import { useEffect, useState } from 'react';

interface ManageMetadataVisibilityToggleButtonProps {
  metadata: MetadataDetailsViewFragment;
}

export const ManageMetadataVisibilityToggleButton = ({
  metadata,
}: ManageMetadataVisibilityToggleButtonProps) => {
  const { showErrorNotification } = useNotificationContext();

  const [isHidden, setIsHidden] = useState(metadata.isHidden);

  const [updateMetadata, { isLoading }] = useUpdateMetadata({
    onError: () => {
      showErrorNotification({
        contentComponent: `Failed to toggle the visibility of the metadata: "${metadata.displayText}"`,
        props: {},
      });
      setIsHidden((prev) => !prev);
    },
  });

  useEffect(() => {
    setIsHidden(metadata.isHidden);
  }, [metadata.isHidden]);

  const toggleVisibility = () => {
    setIsHidden((prev) => !prev);

    const isToggleToHide = !isHidden;

    updateMetadata({
      key: metadata.key,
      source: metadata.source,
      namespace: metadata.namespace,
      isHidden: !isHidden,
      isImportant: isToggleToHide ? false : undefined,
    });
  };

  const getTooltipTitle = () => {
    if (isLoading) {
      return null;
    }

    return `${isHidden ? 'Show' : 'Hide'} the metadata on the platform.`;
  };

  return (
    <CanAccess
      resource={PermissionResource.Metadata}
      operation={PermissionOperation.Write}
      tooltipPlacement="top"
    >
      <Tooltip
        arrow
        title={getTooltipTitle()}
        placement="top"
        disableInteractive
      >
        <Box
          component="span"
          onClick={(e) => {
            e.stopPropagation(); // Prevents opening of edit metadata drawer
          }}
          sx={{
            cursor: 'default', // Prevents the cursor from changing to pointer when button is disabled
          }}
        >
          <IconButton
            onClick={toggleVisibility}
            disabled={isLoading}
            sx={{
              color: isHidden ? 'text.secondary' : 'success.light',
            }}
          >
            {isHidden ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
          </IconButton>
        </Box>
      </Tooltip>
    </CanAccess>
  );
};
